import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, EditableText, Icon, Image, P } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import useClient from 'core/ui/utils/useClient';
import { ConfirmTestModeModal } from './modals/ConfirmTestModeModal';
import { AddIpAddressWarningModal } from './modals/AddIpAddressWarningModal';
import { ConfirmCampaignLiveModal } from './modals/ConfirmCampaignLiveModal';
import { useMoveToStatus } from '../hooks/useMoveToStatus';
import { useCampaignSettings } from '../tabs/CampaignWorkflowTab/state/CampaignSettings/hooks/useCampaignSettings';
import { useHasUnsavedChanges } from '../tabs/CampaignWorkflowTab/state/CampaignSettings/selectors/useHasUnsavedChanges';
import { useConfirmUnsavedChangesModal } from '../tabs/CampaignWorkflowTab/contexts';
import { OnsiteCampaignStatus } from 'sections/onsite-campaigns/types';

const ContentElement = styled.div`
  background-color: ${({ theme }) => theme.vars.newColor.darkGS1};
  padding: 9px 40px 10px 10px;
  width: 100%;
  @media (max-width: 768px) {
    padding: 16px;
  }
  @media (min-width: 768px) {
    display: grid;
    grid-template-areas: 'leftSide centerSide rightSide';
    grid-template-columns: 25% 50% 25%;
  }
`;

export const LogoBox = styled.div`
  height: 50px;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RightSide = styled.div`
  display: flex;
  grid-area: rightSide;
  align-items: center;
  justify-content: end;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
  @media (min-width: 768px) {
    gap: 16px;
  }
  button {
    @media (max-width: 768px) {
      width: 100%;
      :hover {
        margin-bottom: 10px;
      }
    }
  }
`;

export const CenterContent = styled.div`
  grid-area: centerSide;
  display: flex;
  justify-content: center;
  @media (max-width: 768px) {
    margin: 10px 0;
  }
`;

export const LeftSide = styled.div`
  display: flex;
  grid-area: leftSide;
  align-items: center;
  button {
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  svg {
    transform: rotate(180deg);
  }
`;

export const ModalContainer = styled.div`
  display: block;
`;

interface Props {
  onExitButtonClicked?: () => void;
  isError: boolean;
}

export function CampaignHeaderBar(props: Props) {
  const { t } = useTranslation('campaigns');

  const { openConfirmUnsavedChangesModal, setNextAction } = useConfirmUnsavedChangesModal();
  const moveToStatus = useMoveToStatus();
  const { name: campaignName, status: campaignStatus, allowList, setName } = useCampaignSettings();

  const [focusMode, setFocusMode] = useState(false);

  const [confirmTestModeModalOpen, setConfirmTestModeModalOpen] = useState(false);
  const closeConfirmTestModeModal = useCallback(() => setConfirmTestModeModalOpen(false), []);
  const openConfirmTestModeModal = useCallback(() => setConfirmTestModeModalOpen(true), []);

  const [addIpAddressWarningModalOpen, setAddIpAddressWarningModalOpen] = useState(false);
  const closeAddIpAddressWarningModal = useCallback(() => setAddIpAddressWarningModalOpen(false), []);

  const [confirmCampaignLiveModalOpen, setConfirmCampaignLiveModalOpen] = useState(false);
  const closeConfirmCampaignLiveModal = useCallback(() => setConfirmCampaignLiveModalOpen(false), []);

  const hasUnsavedChanges = useHasUnsavedChanges();

  const moveToTestMode = useCallback(() => {
    const onMoveToTestMode = () => {
      moveToStatus(OnsiteCampaignStatus.TEST, () => {
        openConfirmTestModeModal();
      });
    };

    if (hasUnsavedChanges) {
      setNextAction(onMoveToTestMode);
      openConfirmUnsavedChangesModal();
    } else {
      onMoveToTestMode();
    }
  }, [hasUnsavedChanges, moveToStatus, openConfirmTestModeModal, openConfirmUnsavedChangesModal, setNextAction]);

  const revertToDraft = useCallback(() => {
    moveToStatus(OnsiteCampaignStatus.DRAFT);
  }, [moveToStatus]);

  const onPublishButtonClicked = useCallback(() => {
    moveToStatus(OnsiteCampaignStatus.LIVE);
    closeConfirmCampaignLiveModal();
  }, [closeConfirmCampaignLiveModal, moveToStatus]);

  const theme = useTheme();
  const client = useClient();
  const campaignNameValidationState: { message: string; status: 'warning' | 'complete' } = useMemo(() => {
    const minAllowedChar = 5;
    const maxAllowedChar = 100;
    if ((campaignName?.length > 0 && campaignName?.length < minAllowedChar) || campaignName?.length > maxAllowedChar) {
      return {
        message: 'Name should be between 5 - 100 characters',
        status: 'warning',
      };
    }

    return {
      message: null,
      status: 'complete',
    };
  }, [campaignName]);

  return (
    <ContentElement>
      <LeftSide>
        <LogoBox>
          <Link to={`/`}>
            <Image src="/images/slim-logo.svg" alt="SaleCycle Logo" />
          </Link>
        </LogoBox>
        <Button
          onClick={props.onExitButtonClicked}
          variation="secondaryDark"
          leftSideIcon={<Icon icon="arrow-right" />}
          title="Exit Campaign Editor"
        />
      </LeftSide>
      <CenterContent>
        <EditableText
          style={{ backgroundColor: 'unset' }}
          ariaLabel="Campaign name"
          value={campaignName}
          onChange={setName}
          placeholder={'Enter a campaign name (5 - 100 characters)'}
          darkMode={true}
          Icon={<Icon icon="edit" color={'#586874'} />}
          onFocus={() => setFocusMode(true)}
          onBlur={() => setFocusMode(false)}
          maxLength={100}
          focusMode={focusMode}
          spacingBelow={false}
          hasCharCount={true}
          minWidth={'315px'}
          maxWidth={'315px'}
          disabled={(campaignStatus === 'Test' || campaignStatus === 'Live') && allowList?.length > 0}
          charLength={focusMode && campaignName?.length}
          validationMessage={!focusMode ? campaignNameValidationState.message : ''}
          validationState={focusMode && campaignNameValidationState.status}
        />
      </CenterContent>
      {!props.isError && (
        <RightSide>
          <P color={theme.vars.newColor.white}>
            <span aria-label="Client name" style={{ fontWeight: 'bold', paddingRight: '12px' }}>
              {t('onsite.campaignStatus.clientLabel')}
              {client?.name}
            </span>
          </P>
          {campaignStatus === 'Test' && allowList?.length ? (
            <Button
              onClick={revertToDraft}
              variation={'secondaryDark'}
              ariaLabel={t('onsite.campaignStatus.revertToDraft')}
            >
              {t('onsite.campaignStatus.revertToDraft')}
            </Button>
          ) : (
            <Button
              onClick={moveToTestMode}
              variation={'primary'}
              ariaLabel={t('onsite.campaignStatus.test')}
              disabled={hasUnsavedChanges}
            >
              {t('onsite.campaignStatus.test')}
            </Button>
          )}
          <Button
            disabled={!(campaignStatus === 'Test')}
            onClick={onPublishButtonClicked}
            ariaLabel={t('onsite.campaignStatus.publish')}
            variation="primary"
          >
            {t('onsite.campaignStatus.publish')}
          </Button>
        </RightSide>
      )}

      <ConfirmTestModeModal open={confirmTestModeModalOpen} onClose={closeConfirmTestModeModal} />
      <AddIpAddressWarningModal open={addIpAddressWarningModalOpen} onClose={closeAddIpAddressWarningModal} />
      <ConfirmCampaignLiveModal
        open={confirmCampaignLiveModalOpen}
        onClose={closeConfirmCampaignLiveModal}
        onExitButtonClicked={props.onExitButtonClicked}
      />
    </ContentElement>
  );
}
